import React from 'react';

import { css, keyframes } from '@emotion/react';
import { CssVars, NeutralColorToken, TextButton, Type } from '@robinhood/design';
import { MoneyDollarGranularityMicro } from '@x1/util-format';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';

import CardReveal from './CardReveal';
import GoldIcon from 'src/assets/vectors/GoldIcon';
import { GoldMembershipPageQuery as GoldMembershipPageQueryType } from 'src/clients/Network/gql/build/GoldMembershipPageQuery.graphql';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import GradientBackground from 'src/components/creditApplication/GradientBackground';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import LoadingPage from 'src/views/creditApplication/LoadingPage';

const GoldMembershipPageQuery = graphql`
  query GoldMembershipPageQuery {
    authIdentity {
      id
      goldContext {
        subscriptionStatus
        annualFeeMicro
      }
    }
  }
`;

const GoldMembershipPage = () => {
  const { nextPage, nextPageLoading } = React.useContext(NavigationContext);
  const { authIdentity } = useLazyLoadQuery<GoldMembershipPageQueryType>(
    GoldMembershipPageQuery,
    {},
    { fetchPolicy: 'network-only' },
  );
  const annualFeeMicro = authIdentity.goldContext.annualFeeMicro;
  const hasGold = authIdentity.goldContext.subscriptionStatus === 'GOLD';
  const [showAnimation, setShowAnimation] = React.useState(false);
  const [isFadingOut, setIsFadingOut] = React.useState(false);

  const onAgree = React.useCallback(() => {
    setIsFadingOut(true);
    // Start animation after fade-out completes
    setTimeout(() => {
      setShowAnimation(true);
    }, 1000);
  }, []);

  const onAnimationComplete = React.useCallback(() => {
    nextPage();
  }, [nextPage]);

  if (nextPageLoading) {
    return <LoadingPage loadingText='Just a moment' />;
  }
  return (
    <div css={container}>
      <GradientBackground>
        {showAnimation ? (
          <CardReveal animationState='out' onComplete={onAnimationComplete} />
        ) : (
          <div css={isFadingOut && fadeOut}>
            <CreditApplicationPageLayout title='Exclusively for Robinhood&nbsp;Gold members'>
              <div css={root}>
                <div css={goldDividerContainer}>
                  <div css={divider} />
                  <GoldIcon />
                  <div css={divider} />
                </div>
                <div css={textContainer}>
                  <div css={{ display: 'flex', flexDirection: 'column' }}>
                    <Type.Display size='S' color={NeutralColorToken.Fg2}>
                      Annual Gold Subscription
                    </Type.Display>
                    <Type.Text size='S' color={NeutralColorToken.Fg2}>{`${MoneyDollarGranularityMicro(
                      annualFeeMicro,
                    )}/yr for your first year`}</Type.Text>
                  </div>
                  {hasGold ? (
                    <Type.Text size='S' color={NeutralColorToken.Fg2}>
                      Your brokerage account will be credited for the unused days of your monthly plan.
                    </Type.Text>
                  ) : (
                    <TextButton
                      variant='standalone'
                      type='anchor'
                      target='_blank'
                      label='Learn more about Robinhood Gold'
                      href='https://robinhood.com/gold'
                      color={NeutralColorToken.Fg1}
                    />
                  )}
                </div>
                <Type.Text size='S' color={NeutralColorToken.Fg2}>
                  The Gold Card requires an annual Robinhood Gold subscription to maintain the card and does not include
                  a 30 day free trial. Robinhood Gold is a subscription-based membership program of premium services
                  offered through Robinhood Gold, LLC.
                </Type.Text>
                <Button
                  label='Agree'
                  width='full'
                  type='submit'
                  onClick={onAgree}
                  overrideRootStyles={{ marginTop: 40 }}
                />
              </div>
            </CreditApplicationPageLayout>
          </div>
        )}
      </GradientBackground>
    </div>
  );
};

const container = css`
  width: 100vw;
  height: 100vh;
  background-color: ${CssVars.neutral.bg1};
`;

const root = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  text-align: center;
`;

const textContainer = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const goldDividerContainer = css`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 20px;
`;

const divider = css`
  flex: 1;
  border-bottom: 1px solid ${CssVars.neutral.bg3};
`;

const fadeOutAnimation = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const fadeOut = css`
  animation: ${fadeOutAnimation} 1s ease-out forwards;
`;

const WithSuspense = () => {
  return (
    <React.Suspense fallback={<LoadingPage />}>
      <GoldMembershipPage />
    </React.Suspense>
  );
};

export default WithSuspense;
